import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { filter } from 'rxjs/operators';
import { onUserLogout } from 'src/app/Services/datadog-session';

import { environment } from '../environments/environment';
import { CommonServiceService } from './common-service.service';
import { MasterDataSeriveService } from './master-data-serive.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private inactivityTimer: any;
  private protectedActivityTimer: any;
  private heartbeatTimer: any;
  private readonly defaultHeartbeatIntervalMs = 60000;
  private consecutiveUnhealthyHeartbeats = 0;
  private readonly unhealthyHeartbeatWarnThreshold = 5;
  private readonly protectedActivities = new Set<string>();
  private eventListenersAdded = false;
  isLoggedOut = false;

  constructor(
    private cookieService: CookieService,
    private commonService: CommonServiceService,
    private router: Router,
    private masterDataService: MasterDataSeriveService
  ) {
    // Don't eagerly start the timer — wait for the first NavigationEnd
    // so we know which module the user is actually in.
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      if (this.isAdminModule()) {
        // Entering admin module: clear any running timer and remove
        // lastActivityTime so a stale timestamp can't trigger logout
        // when the app reloads later.
        this.stopSessionTimer();
        this.stopHeartbeatTimer();
        localStorage.removeItem('lastActivityTime');
      } else {
        // Production module: ensure activity tracking is active and
        // start/restart the session timer.
        this.trackUserActivity();
        this.startSessionTimer();
        this.startHeartbeatTimer();
      }
    });
  }

  private isAdminModule(): boolean {
    const path = (window && window.location && window.location.pathname) || '';
    const routerUrl = (this.router && this.router.url) || '';
    return path.startsWith('/admin') || routerUrl.startsWith('/admin');
  }

  /** Stop the inactivity timer without triggering logout. */
  stopSessionTimer() {
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = null;
  }

  startSessionTimer() {
    if (this.isLoggedOut) return;

    // Only enforce session timeout in the production module
    if (this.isAdminModule()) {
      this.stopSessionTimer();
      return;
    }

    const timeoutMinutesStr = localStorage.getItem('sessionTimeoutMinutes');
    const lastActivityTimeStr = localStorage.getItem('lastActivityTime');
    if (!timeoutMinutesStr || isNaN(Number(timeoutMinutesStr))) return;

    const timeoutMinutes = Number(timeoutMinutesStr);
    const lastActivityTime = lastActivityTimeStr ? Number(lastActivityTimeStr) : Date.now();
    const elapsedMinutes = (Date.now() - lastActivityTime) / 60000;
    const remainingMinutes = timeoutMinutes - elapsedMinutes;

    clearTimeout(this.inactivityTimer);
    if (remainingMinutes > 0) {
      this.inactivityTimer = setTimeout(() => this.logout(), remainingMinutes * 60000);
    } else {
      this.logout();
    }
  }

  trackUserActivity() {
    if (this.eventListenersAdded) return;
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, () => {
        // Only track activity in the production module
        if (!this.isAdminModule()) {
          this.touchActivity();
        }
      });
    });
    this.eventListenersAdded = true;
  }

  beginProtectedActivity(activityKey: string): void {
    if (!activityKey) return;

    this.protectedActivities.add(activityKey);
    this.touchActivity();

    if (this.protectedActivityTimer) {
      return;
    }

    this.protectedActivityTimer = setInterval(() => {
      if (this.protectedActivities.size > 0) {
        this.touchActivity();
      }
    }, 30000);
  }

  endProtectedActivity(activityKey: string): void {
    if (!activityKey) return;

    this.protectedActivities.delete(activityKey);
    this.touchActivity();

    if (this.protectedActivities.size === 0 && this.protectedActivityTimer) {
      clearInterval(this.protectedActivityTimer);
      this.protectedActivityTimer = null;
    }
  }

  private touchActivity(): void {
    if (this.isLoggedOut) return;
    if (this.isAdminModule()) return;

    localStorage.setItem('lastActivityTime', Date.now().toString());
    this.startSessionTimer();
  }

  logout() {
    // Don't auto-logout if we're in the admin module
    if (this.isAdminModule()) {
      this.stopSessionTimer();
      return;
    }

    if (this.protectedActivities.size > 0) {
      this.touchActivity();
      return;
    }

    // Check if user already initiated logout manually - if so, don't show auto-logout prompts
    const isUserInitiatedLogout = sessionStorage.getItem('isUserInitiatedLogout') === 'true';
    if (isUserInitiatedLogout) {
      return;
    }

    // Set logout cookie for shared domain
    const domain = environment.domainName;
    const companyweburl = environment.companyWebBaseUrl;
    this.stopHeartbeatTimer();
    onUserLogout();

    const performLogout = () => {
      localStorage.clear();
      sessionStorage.clear();
      this.cookieService.deleteAll('/', domain);
      document.cookie = `logoutEvent=1; domain=${domain}; path=/; max-age=10`;
      window.location.href = `${companyweburl}/shared/login`;
    };

    const isBypassLicense = sessionStorage.getItem('isBypassLicense') === 'true';
    if (isBypassLicense) {
      performLogout();
    } else {
      this.commonService.unassignStationLicenseAndContinue(performLogout, performLogout);
    }
  }

  resetLogoutFlag() {
    this.isLoggedOut = false;
  }

  private startHeartbeatTimer(): void {
    this.stopHeartbeatTimer();
    this.runHeartbeat();
  }

  // AB#13334: self-reschedules with setTimeout (using the server's own
  // RetryAfterSeconds) instead of setInterval, so a slow response can't
  // stack up overlapping calls. Only an explicit 'Revoked' status - a
  // positive signal that the session actually ended - triggers logout.
  // 'Unknown' and transport errors just retry: AB#14151 happened precisely
  // because a single ambiguous/failed heartbeat was treated as "log out".
  private runHeartbeat(): void {
    if (this.isLoggedOut || this.isAdminModule()) {
      return;
    }

    const sessionId = localStorage.getItem('stationSessionId');
    const isBypassLicense = sessionStorage.getItem('isBypassLicense') === 'true';
    if (!sessionId || isBypassLicense) {
      this.heartbeatTimer = setTimeout(() => this.runHeartbeat(), this.defaultHeartbeatIntervalMs);
      return;
    }

    this.masterDataService.sendLicenseHeartbeat(sessionId).subscribe({
      next: res => {
        const intervalMs = (res?.retryAfterSeconds || 60) * 1000;

        // Queue the next beat before reacting to this one. logout() declines
        // to act while a protected activity is running or when the user
        // already initiated logout, and on those paths it returns without
        // stopping this timer - so scheduling first is what keeps the loop
        // alive instead of stalling here forever. A logout that does go
        // through calls stopHeartbeatTimer() and cancels what we queued.
        this.heartbeatTimer = setTimeout(() => this.runHeartbeat(), intervalMs);

        if (res?.status === 'Revoked') {
          this.logout();
          return;
        }
        if (res?.status === 'Active') {
          this.consecutiveUnhealthyHeartbeats = 0;
        } else {
          this.noteUnhealthyHeartbeat('Unknown session');
        }
      },
      error: () => {
        this.noteUnhealthyHeartbeat('Heartbeat request failed');
        this.heartbeatTimer = setTimeout(() => this.runHeartbeat(), this.defaultHeartbeatIntervalMs);
      }
    });
  }

  // Retrying forever is deliberate - never log a working user out over an
  // ambiguous answer. But a session that is permanently Unknown means the
  // user is working unlicensed with nothing surfacing it, so make the
  // streak visible instead of failing silently forever.
  private noteUnhealthyHeartbeat(reason: string): void {
    this.consecutiveUnhealthyHeartbeats++;
    if (this.consecutiveUnhealthyHeartbeats % this.unhealthyHeartbeatWarnThreshold === 0) {
      console.warn(
        `Station license heartbeat unhealthy for ${this.consecutiveUnhealthyHeartbeats} consecutive attempts (${reason}). Session is not being tracked; continuing without logout.`
      );
    }
  }

  stopHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}
